import { useEffect, useState, useRef } from "react";
import { AuthProvider, UserManager } from "oidc-react";
import handleUser from ".././functions/userIdentity/handleUser";
import { setUserDetails } from "../redux/actions/setUserDetailsAction";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getFirebaseLicenseFile } from "../redux/actions/Firebase/getFirebaseLicenseAction";
import { getNotificationSettings } from "../redux/actions/Settings/getNotificationSettingAction";
import axios from "../api/axiosConfig";
import LoadingScreen from "../components/loadingScreen/LoadingScreen";
import { setUserSettings } from '../redux/actions/Settings/setSettingslAction';
import { setTheme } from '../functions/themeService/themeService';
import { Capacitor } from "@capacitor/core";
import { Browser } from '@capacitor/browser';
import { Preferences } from '@capacitor/preferences';
import { App } from '@capacitor/app';
import signOut from "../functions/userIdentity/signOut";
import { jwtDecode } from 'jwt-decode';
import { saveCalendarSettings } from "../redux/actions/Calendar/saveCalendarSettingsAction";
import { getCalendarColors } from "../redux/actions/Calendar/getCalendarColorsAction";
import { useIsMobile } from "../functions/deviceIdentity/getDeviceType";
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../firebase/firebaseConfig';
import { setItem, getItem } from "../utils/storageHelper";
import {
    openNativeAuthSession,
    persistLoginPkceState,
    restoreLoginPkceState,
    clearLoginPkceState,
    isValidLoginPkceCallback,
} from "../utils/nativeAuthBrowser";
import * as Sentry from "@sentry/react";
import {
    isBackForwardNavigation,
    isBackForwardPageRestore,
    consumeBackForwardNavigation,
    shouldReturnToWelcomeFromAbortedLogin,
    clearStaleOidcClientState,
} from "./loginNavigationHelpers";

const OIDC_USER_KEY = () =>
    `oidc.user:${import.meta.env.VITE_PUBLIC_IDP_ROOT}:${import.meta.env.VITE_PUBLIC_IDP_CLIENT}`;

const ANDROID_CALLBACK_GRACE_MS = 3000;

export default () => {
    const [redirectToOIDC, setRedirectToOIDC] = useState(false);
    const [oidcConfig, setOidcConfig] = useState({});
    const [userLoginStatus, setUserLoginStatus] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("");
    const authInProgress = useRef(false);
    const authCallbackReceived = useRef(false);
    const browserCloseGraceTimerRef = useRef(null);
    const appUrlOpenListenerRef = useRef(null);
    const appUrlOpenListenerOperationRef = useRef(Promise.resolve());
    const loginActiveRef = useRef(true);
    const navigate = useNavigate();

    const clearBrowserCloseGraceTimer = () => {
        if (browserCloseGraceTimerRef.current) {
            clearTimeout(browserCloseGraceTimerRef.current);
            browserCloseGraceTimerRef.current = null;
        }
    };
    const dispatch = useDispatch();
    const isMobile = useIsMobile();
    const licenseChecking = useSelector(state => state.license.licenseChecking);
    let environment = window.localStorage.getItem("environment") ? window.localStorage.getItem("environment") : window.localStorage.setItem("environment", import.meta.env.VITE_PUBLIC_APP_ENV);
    const idpScope = "openid protected_email email profile external_user_id offline_access";

    const navigateToDefaultView = async (settings) => {
        if (settings) {
            switch (settings.defaultView) {
                case 'overview':
                    navigate('/Overview');
                    break;
                case 'dueDates':
                    navigate('/Due-dates');
                    break;
                case 'todo':
                    navigate('/Todo');
                    break;
                case 'calendar':
                    if (Capacitor.isNativePlatform()) {
                        navigate('/Overview');
                    } else {
                        navigate('/Calendar');
                    }
                    break;
                default:
                    navigate('/Overview');
                    break;
            }
        } else {
            navigate('/Overview');
        }
    };

    useEffect(() => {
        if (!Capacitor.isNativePlatform()) {
            return;
        }

        let backButtonHandle;
        let cancelled = false;

        App.addListener('backButton', async () => {
            if (authCallbackReceived.current) {
                return;
            }
            clearBrowserCloseGraceTimer();
            authInProgress.current = false;
            loginActiveRef.current = false;
            await clearLoginPkceState();
            if (cancelled) {
                return;
            }
            navigate('/Welcome', { replace: true });
        }).then((handle) => {
            if (cancelled) {
                handle.remove();
                return;
            }
            backButtonHandle = handle;
        }).catch((error) => {
            if (!cancelled) {
                console.error('[NATIVE LOGIN] Failed to set up backButton listener:', error);
            }
        });

        return () => {
            cancelled = true;
            backButtonHandle?.remove();
        };
    }, [navigate]);

    useEffect(() => {
        if (Capacitor.isNativePlatform()) {
            return;
        }

        const handlePageShow = (event) => {
            if (!isBackForwardPageRestore(event)) {
                return;
            }

            consumeBackForwardNavigation();

            const oidcUserData = window.sessionStorage.getItem(OIDC_USER_KEY());
            if (!shouldReturnToWelcomeFromAbortedLogin(oidcUserData)) {
                return;
            }

            loginActiveRef.current = false;
            clearStaleOidcClientState();
            navigate('/Welcome', { replace: true });
        };

        window.addEventListener('pageshow', handlePageShow);
        return () => window.removeEventListener('pageshow', handlePageShow);
    }, [navigate]);

    useEffect(() => {
        let cancelled = false;
        loginActiveRef.current = true;
        const enqueueListenerOperation = (operation) => {
            const result = appUrlOpenListenerOperationRef.current.then(operation);
            appUrlOpenListenerOperationRef.current = result.catch(() => {});
            return result;
        };

        if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
            enqueueListenerOperation(async () => {
                const previousHandle = appUrlOpenListenerRef.current;
                appUrlOpenListenerRef.current = null;
                await previousHandle?.remove();
                if (cancelled) {
                    return;
                }
                const handle = await App.addListener('appUrlOpen', async (data) => {
                    if (cancelled) {
                        return;
                    }
                    console.log('[NATIVE LOGIN] appUrlOpen received:', data?.url ?? 'no url');
                    if (data?.url) {
                        const urlString = data.url;
                        if (urlString.startsWith(import.meta.env.VITE_PUBLIC_REDIRECT_URI)) {
                            clearBrowserCloseGraceTimer();
                            authCallbackReceived.current = true;
                            await nativeCallback(urlString);
                        } else {
                            navigate('/Welcome');
                        }
                    } else {
                        navigate('/Welcome');
                    }
                });
                if (cancelled) {
                    await handle.remove();
                    return;
                }
                appUrlOpenListenerRef.current = handle;
            }).catch((error) => {
                if (!cancelled) {
                    console.error('[NATIVE LOGIN] Failed to set up appUrlOpen listener:', error);
                }
            });
        }

        async function returnToWelcomeFromAbortedLogin() {
            clearStaleOidcClientState();
            if (Capacitor.isNativePlatform()) {
                await clearLoginPkceState();
            }
            navigate('/Welcome', { replace: true });
        }

        async function fetchData() {
            try {
                const firebaseUserString = Capacitor.isNativePlatform()
                    ? await getItem(`firebase:user:${import.meta.env.VITE_PUBLIC_APP_ENV}`)
                    : window.localStorage.getItem(
                        `firebase:user:${import.meta.env.VITE_PUBLIC_APP_ENV}`
                    );

                if (cancelled) {
                    return;
                }

                if (auth.currentUser || firebaseUserString) {
                    const storedSettings = Capacitor.isNativePlatform()
                        ? await getItem(`usersettings:${import.meta.env.VITE_PUBLIC_APP_ENV}`)
                        : window.localStorage.getItem(
                            `usersettings:${import.meta.env.VITE_PUBLIC_APP_ENV}`
                        );

                    if (storedSettings) {
                        await navigateToDefaultView(JSON.parse(storedSettings));
                    } else {
                        navigate('/Overview');
                    }
                    return;
                }

                let data = null;
                if (Capacitor.isNativePlatform()) {
                    const { value } = await Preferences.get({ key: OIDC_USER_KEY() });
                    data = value;
                } else {
                    data = window.sessionStorage.getItem(OIDC_USER_KEY());
                }

                if (cancelled) {
                    return;
                }

                if (!Capacitor.isNativePlatform() && isBackForwardNavigation()) {
                    consumeBackForwardNavigation();
                    if (shouldReturnToWelcomeFromAbortedLogin(data)) {
                        await returnToWelcomeFromAbortedLogin();
                        return;
                    }
                }

                if (typeof data === "undefined" || data === null) {
                    redirectUserToOIDC();
                } else {
                    setUserLoginStatus(true);
                    const user = JSON.parse(data);
                    if (!Capacitor.isNativePlatform()) {
                        localStorage.setItem("provider", user.profile.idp);
                    }
                    await handleFirebaseLogin(user);
                }
                return;
            } catch (e) {
                if (cancelled) {
                    return;
                }
                // eslint-disable-next-line no-use-before-define
                redirectUserToOIDC();
                return;
            }
        }
        fetchData();

        return () => {
            cancelled = true;
            loginActiveRef.current = false;
            clearBrowserCloseGraceTimer();
            enqueueListenerOperation(async () => {
                const handle = appUrlOpenListenerRef.current;
                appUrlOpenListenerRef.current = null;
                await handle?.remove();
            });
        };
    }, [redirectToOIDC]);

    const handleFirebaseLogin = async (user, json) => {
        try {
            let provider = localStorage.getItem("provider");
            if (!provider || provider === '""') {
                if (!Capacitor.isNativePlatform()) {
                    localStorage.setItem("provider", JSON.stringify(user.profile.idp));
                }
                else {
                    
                    const decoded_idToken = jwtDecode(user.id_token);
                    await Preferences.set({ key: "provider", value: decoded_idToken.idp });
                    setItem("environment", import.meta.env.VITE_PUBLIC_APP_ENV);
                }
                
            }

            const resolvedProvider = await getItem("provider");
            if (resolvedProvider) {
                Sentry.setTag("login_provider", resolvedProvider.replace(/"/g, ''));
            }

      // Step 1: Get custom token from server
      const customTokenResponse = await axios.post(
        `${import.meta.env.VITE_PUBLIC_DOMAIN}Firebase/userDetails`,
          {
              accessToken: user.access_token
          },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      )

      if (customTokenResponse.status !== 200) {
          // Clear oidc access token
          window.sessionStorage.removeItem(OIDC_USER_KEY());
          await Preferences.clear();
          await Browser.close();
          await signOut();
          return;
      }
      
      const customTokenData = customTokenResponse.data;
      
      // Step 2: Sign into Firebase client-side using custom token
      const firebaseUserCredential = await signInWithCustomToken(auth, customTokenData.customToken);
      const firebaseUser = firebaseUserCredential.user;
      
      // Step 3: Get Firebase ID token
      const idToken = await firebaseUser.getIdToken();
      
      // Step 4: Send Firebase ID token to server to set user details
      // NOTE: Refresh token is NOT sent to server - each device manages its own Firebase session
      const setUserDetailsResponse = await axios.post(
        `${import.meta.env.VITE_PUBLIC_DOMAIN}Firebase/setUserDetailsFromToken`,
        {
          idToken: idToken,
          externalUserId: customTokenData.externalUserId
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      
      if (setUserDetailsResponse.status === 200) {
        const firebaseUserJson = setUserDetailsResponse.data;
        // Use setItem for cross-platform storage (Capacitor Preferences on native, localStorage on web)
        await setItem(
          `firebase:user:${import.meta.env.VITE_PUBLIC_APP_ENV}`,
          JSON.stringify(firebaseUserJson.firebase_details)
        );
        const userDetails = {
          name: firebaseUserJson.firebase_details.Name || customTokenData.name,
          email: firebaseUserJson.firebase_details.Email || customTokenData.email,
          photoUrl: firebaseUserJson.firebase_details.FirebasePhotoUrl || ""
        }
        dispatch(
          setUserDetails(userDetails)
        );
      }

            // From this point forward, axios will automatically add Firebase token to headers
            const settingsResponse = await axios.get(
                `${import.meta.env.VITE_PUBLIC_DOMAIN}User/getSettings`,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                });

            const settingsJson = settingsResponse.data;

            await setItem(
                `usersettings:${import.meta.env.VITE_PUBLIC_APP_ENV}`,
                JSON.stringify(settingsJson)
            );

            dispatch(setUserSettings(settingsJson));

            const calendarSettingsResponse = await axios.get(
                `${import.meta.env.VITE_PUBLIC_DOMAIN}User/getCalendarSettings`,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                });

            const calendarSettingsJson = calendarSettingsResponse.data;

            await dispatch(saveCalendarSettings(calendarSettingsJson));
            await dispatch(getCalendarColors());

            setTheme(settingsJson.theme);

            // CRITICAL: Wait for license check to fully complete before navigating
            // This prevents race conditions on first sign-in where modal appears for licensed users
            if (!licenseChecking) {
                dispatch({ type: "LICENSE_CHECKING", payload: true });
                await dispatch(getFirebaseLicenseFile());
                
                // Extra delay on native platforms to ensure storage write completes
                if (Capacitor.isNativePlatform()) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                }
            }

            dispatch(getNotificationSettings());

            await navigateToDefaultView(settingsJson);

    } catch (error) {
      if (error.response) {
          const errorCode = error.response.status || "Unknown";
          const errorMessage = error.response.data?.message || "An error occurred";
       
        // Only capture unexpected errors in Sentry (not auth failures or user cancellations)
        if (errorCode !== 401 && errorCode !== 403 && 
            !error.message?.includes('user cancelled') &&
            !error.message?.includes('user canceled')) {
            
            Sentry.captureException(error, {
                tags: {
                    error_type: 'login_unexpected_failure',
                    operation: 'webLogin',
                    is_native: Capacitor.isNativePlatform(),
                    error_code: errorCode
                },
                level: 'error',
                extra: {
                    error_message: errorMessage
                }
            });
        }
       
        if (Capacitor.isNativePlatform()) {
          await Preferences.clear();
          }
          console.error("Error:", error.message || 'Unknown error');
          sessionStorage.clear();
          localStorage.clear();
          navigate(`/Error?errorCode=${encodeURIComponent(errorCode)}&errorMessage=${encodeURIComponent(errorMessage)}`);
        
      }
      console.error("Error:", error.message || 'Unknown error');
    }
  };

    const signInFunc = async (user) => {
        //const userDetailsFound = await handleUser(user);

      if (user == null) {
          // eslint-disable-next-line no-use-before-define
          redirectUserToOIDC();
      }
      else {
          await handleFirebaseLogin(user);
      }
  };

  const tcWrapper = function (f) {
    return function () {
      try {
        // eslint-disable-next-line prefer-rest-params
        f.apply(this, arguments)
            .then((user) => {
                if (user === null)
                {
                    console.log("TC Wrapper");
                }
                localStorage.setItem("provider", user.profile.idp);
              signInFunc(user);

          })
          .catch((err) => {
            console.error("tcWrapper error: ", err.message);
            signInFunc(null);
          });
      } catch (e) {
        console.log(e);
      }
    };
  };

    const redirectUserToOIDC = () => {
        if (!loginActiveRef.current) {
            return;
        }

        try {

            if (Capacitor.isNativePlatform()) {
                nativeLogin();
                return;
            }

            const manager = new UserManager({
                authority: `${import.meta.env.VITE_PUBLIC_IDP_ROOT}`,
                client_id: `${import.meta.env.VITE_PUBLIC_IDP_CLIENT}`,
                redirect_uri: `${import.meta.env.VITE_PUBLIC_REDIRECT_URI}`,
                scope: idpScope,
                automaticSilentRenew: false,
                extraQueryParams: {
                    prompt: 'select_account'
                }
            });

            manager.signinCallback = tcWrapper(manager.signinCallback, manager);

            const config = {
                authority: `${import.meta.env.VITE_PUBLIC_IDP_ROOT}`,
                clientId: `${import.meta.env.VITE_PUBLIC_IDP_CLIENT}`,
                responseType: "code",
                redirectUri: `${import.meta.env.VITE_PUBLIC_REDIRECT_URI}`,
                end_session_endpoint: `${import.meta.env.VITE_PUBLIC_IDP_ROOT}/connect/endsession`,
                userManager: manager,
            };

            setOidcConfig(config);
            setRedirectToOIDC(true);

        } catch (err) {
            console.error("redirectUserToOIDC Error", err);
        }
    };

    const nativeLogin = async () => {
        try {
            function base64URLEncode(utf8Bytes) {
                // 1. Convert UTF-8 bytes to a binary string:
                let binaryString = '';
                for (let i = 0; i < utf8Bytes.length; i++) {
                    binaryString += String.fromCharCode(utf8Bytes[i]);
                }
                // 2. Base64 encode the binary string:
                const base64Encoded = btoa(binaryString);
                // 3. URL safe string
                return base64Encoded
                    .replace(/\+/g, '-')
                    .replace(/\//g, '_')
                    .replace(/=/g, '');
            }

            function getRandomBytes(length) {
                if (window.crypto && window.crypto.getRandomValues) {
                    const arr = new Uint8Array(length); // Create a typed array to hold the random bytes
                    window.crypto.getRandomValues(arr); // Fill the array with cryptographically secure random values
                    return arr; // Return the Uint8Array
                } else {
                    console.error("Web Crypto API is not available.");
                    return null;
                }
            }
            const verifier = base64URLEncode(getRandomBytes(32));

            async function sha256Hash(message) {
                const msgBuffer = new TextEncoder().encode(message); // encode as (utf-8) Uint8Array
                const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer); // hash the message
                const hashArray = Array.from(new Uint8Array(hashBuffer)); // convert buffer to byte array
                return hashArray; //return byte array
            }
            const challenge = base64URLEncode(await sha256Hash(verifier));

            const state = (crypto.randomUUID?.() ??
                ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
                    (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
                ));

            await persistLoginPkceState(verifier, state);

            //Manually trigger the signin redirect request
            const authUrl = `${import.meta.env.VITE_PUBLIC_IDP_ROOT}/connect/authorize?` +
                `&response_type=code` +
                `&code_challenge=${challenge}` +
                `&code_challenge_method=S256` +
                `&client_id=${import.meta.env.VITE_PUBLIC_IDP_CLIENT}` +
                `&redirect_uri=${encodeURIComponent(import.meta.env.VITE_PUBLIC_REDIRECT_URI)}` +
                `&scope=${encodeURIComponent(idpScope)}` +
                `&state=${state}` +
                `&prompt=select_account`;

            authCallbackReceived.current = false;
            authInProgress.current = true;

            const callbackUrl = await openNativeAuthSession(
                authUrl,
                import.meta.env.VITE_PUBLIC_REDIRECT_URI,
                {
                    onBrowserFinished: Capacitor.getPlatform() === 'android'
                        ? () => {
                            if (!authInProgress.current || authCallbackReceived.current) {
                                return;
                            }
                            // browserFinished can fire before appUrlOpen on success; never clear PKCE here.
                            console.log('[NATIVE LOGIN] Browser closed during authentication');
                            setLoadingMessage("Completing sign in...");
                            clearBrowserCloseGraceTimer();
                            browserCloseGraceTimerRef.current = setTimeout(async () => {
                                browserCloseGraceTimerRef.current = null;
                                if (authCallbackReceived.current) {
                                    return;
                                }
                                authInProgress.current = false;
                                setLoadingMessage("");
                                await clearLoginPkceState();
                                navigate('/Welcome');
                            }, ANDROID_CALLBACK_GRACE_MS);
                        }
                        : undefined,
                }
            );

            if (callbackUrl) {
                authCallbackReceived.current = true;
                await nativeCallback(callbackUrl);
            }
        } catch (error) {
            authInProgress.current = false;

            if (error?.code === 'CANCELED') {
                console.log('[NATIVE LOGIN] User canceled authentication');
                clearBrowserCloseGraceTimer();
                await clearLoginPkceState();
                navigate('/Welcome');
                return;
            }

            // Capture unexpected native login setup failures
            Sentry.captureException(error, {
                tags: {
                    error_type: 'native_login_setup_failure',
                    operation: 'nativeLogin',
                    platform: Capacitor.getPlatform()
                },
                level: 'error'
            });
            
            await nativeLoginError(`Login error: ${error.message}`, `Login error: ${error.message}`);
        }
    };

    const nativeCallback = async (urlString) => {
        try {
            const { verifier, state } = await restoreLoginPkceState();

            if (urlString.length > 0) {
                const url = new URL(urlString);
                const returnedCode = url.searchParams.get('code');
                const returnedState = url.searchParams.get('state');

                if (isValidLoginPkceCallback({ returnedCode, returnedState, verifier, state })) {
                    try {
                        const response = await axios.post(`${import.meta.env.VITE_PUBLIC_IDP_ROOT}/connect/token`, {
                            client_id: `${import.meta.env.VITE_PUBLIC_IDP_CLIENT}`,
                            code_verifier: `${verifier}`,
                            grant_type: "authorization_code",
                            code: `${returnedCode}`,
                            redirect_uri: `${import.meta.env.VITE_PUBLIC_REDIRECT_URI}`
                        }, {
                            headers: {
                                'Content-Type': 'application/x-www-form-urlencoded'
                            }
                        });
                        if (response.status === 200) {
                            await clearLoginPkceState();
                            const user = response.data;
                            await getNativeUserInfo(user);
                        } else {
                            await nativeLoginError(`Login error (01 - status ${response.status}`, `Error retrieving token: status ${response.status}`);
                        }
                    } catch (error) {
                        // Only capture unexpected token retrieval errors (not auth rejections)
                        if (error.response?.status !== 401 && error.response?.status !== 403) {
                            Sentry.captureException(error, {
                                tags: {
                                    error_type: 'token_exchange_failure',
                                    operation: 'nativeCallback_tokenExchange',
                                    platform: Capacitor.getPlatform()
                                },
                                level: 'error',
                                extra: {
                                    status_code: error.response?.status
                                }
                            });
                        }

                        await nativeLoginError(
                            `Login error (01) ${error.message}`,
                            `Error retrieving token: catch ${error.message}`
                        );
                    }
                } else {
                    const callbackIssues = [
                        !returnedCode && 'missing code',
                        !verifier && 'missing PKCE verifier',
                        !state && 'missing PKCE state',
                        !returnedState && 'missing callback state',
                        state && returnedState && returnedState !== state && `state mismatch (${returnedState}!=${state})`,
                    ].filter(Boolean).join(', ');
                    await nativeLoginError(
                        "Login error (02)",
                        `Invalid authentication callback: ${callbackIssues || 'unknown validation failure'}`
                    );
                }
            } else {
                await nativeLoginError("Login error (03)", "URL is empty");
            }
        } catch (error) {
            // Capture unexpected native callback processing failures
            Sentry.captureException(error, {
                tags: {
                    error_type: 'native_callback_failure',
                    operation: 'nativeCallback',
                    platform: Capacitor.getPlatform()
                },
                level: 'error'
            });
            
            await nativeLoginError(`Login error (04) ${error.message}`, `Login error: ${error.message}`);
        } finally {
            clearBrowserCloseGraceTimer();
            authInProgress.current = false;
        }
    }

    const getNativeUserInfo = async (user) => {
        try {
            if (user) {
                const userString = JSON.stringify(user);
                await Preferences.set({ key: OIDC_USER_KEY(), value: userString });

                const userInfoResponse = await axios.get(
                    `${import.meta.env.VITE_PUBLIC_IDP_ROOT}/connect/userinfo`,
                    {
                        headers: {
                            Authorization: `Bearer ${user.access_token}`,
                        },
                    }
                );

                const json = userInfoResponse.data;
                const isIosAuthSession = Capacitor.getPlatform() === 'ios';

                if (Capacitor.isNativePlatform()) {
                    if (!isIosAuthSession) {
                        setLoadingMessage("Please wait, you'll be redirected to the app shortly. Do not close this window.");
                    }
                    
                    await handleFirebaseLogin(user);
                    
                    // CRITICAL: Wait for Firebase auth state to be fully ready before closing browser
                    // This ensures auth.currentUser is available for axios interceptors
                    let authReady = false;
                    for (let i = 0; i < 10 && !authReady; i++) {
                        await new Promise(resolve => setTimeout(resolve, 300));
                        if (auth.currentUser) {
                            console.log('[NATIVE LOGIN] Firebase auth.currentUser ready');
                            authReady = true;
                        }
                    }
                    
                    if (!authReady) {
                        console.warn('[NATIVE LOGIN] Warning: auth.currentUser still null after waiting');
                    }

                    if (!isIosAuthSession) {
                        await Browser.close();
                        setLoadingMessage("");
                    }
                } else {
                    handleFirebaseLogin(user);
                }
            } else {
                await nativeLoginError("User is not set", "User is not set");
            }
        } catch (error) {
            // Capture unexpected user info retrieval failures
            Sentry.captureException(error, {
                tags: {
                    error_type: 'native_userinfo_failure',
                    operation: 'getNativeUserInfo',
                    platform: Capacitor.getPlatform()
                },
                level: 'error'
            });
            
            await nativeLoginError(`Error getting user info`, `User info error: ${error.message}`);
        }
    }

    const nativeLoginError = async (message, logMessage) => {
        try {
            clearBrowserCloseGraceTimer();
            window.sessionStorage.removeItem(OIDC_USER_KEY());
            await clearLoginPkceState();
            await Preferences.clear();
            if (Capacitor.getPlatform() !== 'ios') {
                await Browser.close();
            }
            navigate(`/Welcome`);
            console.error(logMessage);
            setTimeout(function () {
                window.alert(`Error: ${message}
        Please try again.`);
            }, 1000); //alert on a timeout to allow the lines above to complete first
        } catch (error) {
            console.error(`nativeLoginError: ${error.message}`);
        }
    }

    if (redirectToOIDC) {
        return (
            <AuthProvider {...oidcConfig}>
                <LoadingScreen userLoginStatus={userLoginStatus} message={loadingMessage}></LoadingScreen>
            </AuthProvider>
        );
    } else {
        return <LoadingScreen userLoginStatus={userLoginStatus} message={loadingMessage}></LoadingScreen>;
    }
};
